import { useTranslations } from 'next-intl';

import FlashIcon from '@/assets/icons/account/flash.svg';
import BackIcon from '@/assets/icons/back.svg';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';

import { I_Winner } from './WinnerFlashCard';

interface I_SelectedFlashWinnerTabProps {
  selectedFlashWinner: I_Winner;
  onBack?: () => void;
  shouldShowClaimButton: boolean;
  isAlreadyClaimed: boolean;
  onClaimWinnings: () => void;
  isClaiming: boolean;
  decimals?: number;
}

export function SelectedFlashWinnerTab({
  selectedFlashWinner,
  onBack,
  shouldShowClaimButton,
  isAlreadyClaimed,
  onClaimWinnings,
  isClaiming,
  decimals = 2
}: I_SelectedFlashWinnerTabProps) {
  const t = useTranslations();

  return (
    <div className="mt-2 ">
      <div className="flex gap-4 items-center mb-6">
        {onBack && (
          <Button
            variant="outline"
            size="icon"
            className="w-9 h-9"
            icon={<BackIcon className="text-foreground" />}
            onClick={onBack}
          />
        )}
        {(shouldShowClaimButton || isAlreadyClaimed) && (
          <Button
            variant="brand"
            size="lg"
            className="w-full h-9 uppercase"
            onClick={isAlreadyClaimed ? undefined : onClaimWinnings}
            disabled={isAlreadyClaimed || isClaiming}
          >
            {isAlreadyClaimed
              ? t('profile.predictionWinners.claimed')
              : isClaiming
                ? t('profile.predictionWinners.claiming')
                : t('profile.predictionWinners.claimWin')}
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div>
          <P className="text-foreground text-sm font-semibold leading-5">
            {selectedFlashWinner.name}
          </P>
          <P className="text-foreground text-xs font-light leading-4 whitespace-nowrap">
            {selectedFlashWinner.winningSubmissions.length}{' '}
            {t('profile.predictionWinners.predictions')}
          </P>
        </div>

        <div className="flex items-center gap-2">
          <FlashIcon className="w-4 h-4" />

          <P className="text-foreground text-sm font-semibold leading-5">
            {t('profile.predictionWinners.flashWinner')}
          </P>
        </div>
      </div>

      <div>
        <div className="rounded-10 outline-1 outline-base-border/20 overflow-hidden flex flex-col max-h-100">
          <div className="flex sticky top-0 z-10">
            <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex items-center">
              <P className="flex-1 justify-center text-center text-muted-foreground text-sm font-bold leading-5">
                {t('profile.predictionWinners.prediction')}
              </P>
            </div>

            {
              <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex items-center">
                <P className="flex-1 justify-center text-center text-muted-foreground text-sm font-bold leading-5">
                  {t('profile.filters.won')}
                </P>
              </div>
            }

            <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex items-center">
              <P className="flex-1 justify-center text-center text-muted-foreground text-sm font-bold leading-5">
                {t('profile.predictionWinners.entry')}
              </P>
            </div>
          </div>

          <div className="flex h-40 overflow-y-auto flex-1">
            <div className="flex-1 flex flex-col">
              {selectedFlashWinner.winningSubmissions.map((winning, index) => (
                <div key={index} className="flex items-center">
                  <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex justify-center">
                    <P>
                      {formatNumberWithCommasAndDecimals(
                        winning.price,
                        decimals
                      )}
                    </P>
                  </div>

                  <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex justify-center">
                    <P className="text-primary">
                      + {selectedFlashWinner.winningPricesWon}
                    </P>
                  </div>
                  <div className="flex-1 px-2 py-2.5 border-b border-base-border/20 flex justify-center">
                    <P>{winning.entryTime}</P>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
